import React, { useEffect, useState } from "react";
import { ArrowLeft, TrendingUp, TrendingDown, Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api, { formatError } from "../lib/api";
import CandlestickChart from "./CandlestickChart";
import { logError } from "../lib/logger";

const fmtNum = (v) => (v == null ? "—" : String(v).replace(".", ","));

const INTERVAL_LABEL = { "1min": "1M", "5min": "5M", "15min": "15M", "30min": "30M", "1h": "1H", "2h": "2H", "4h": "4H", "8h": "8H", "1day": "1G", "1week": "1S" };
const STATUS_BADGE = {
  active: { cls: "bg-blue-500/15 text-blue-400", label: "Attivo" },
  tp_hit: { cls: "bg-emerald-500/15 text-emerald-400", label: "Take Profit" },
  sl_hit: { cls: "bg-red-500/15 text-red-400", label: "Stop Loss" },
  be_hit: { cls: "bg-sky-400/20 text-sky-300", label: "Breakeven" },
};

// Header badge reflecting pending / filled LIMIT states or the closed status.
function orderBadge(data) {
  if (!data) return null;
  if (data.order_type === "LIMIT" && !data.filled) return { cls: "bg-white/10 text-white", label: "Pendente" };
  if (data.order_type === "LIMIT" && data.filled) return { cls: "bg-blue-500/15 text-blue-400", label: "Filled" };
  return STATUS_BADGE[data.status];
}

function chartCaption(data) {
  if (data?.order_type === "LIMIT" && !data?.filled)
    return "Ordine LIMIT in attesa — il segnale entrerà quando il prezzo raggiunge la linea di entrata. Dati Capital.com.";
  if (data?.status === "active")
    return "Trade ancora aperto — il grafico è aggiornato al momento attuale. Dati Capital.com.";
  if (data?.status === "be_hit")
    return "Trade chiuso a BREAKEVEN — dopo il breakeven il prezzo è tornato all'entry (né profitto né perdita). Dati Capital.com.";
  const touch = data?.status === "tp_hit" ? "del Take Profit" : "dello Stop Loss";
  return `Il grafico si ferma al momento del tocco ${touch}. Dati Capital.com.`;
}

// Pending LIMIT: dashed line, light-blue (BUY) / red (SELL). Otherwise solid "Entry" line.
function entryLineStyle(data) {
  const buy = data?.direction === "BUY";
  if (data?.order_type === "LIMIT" && !data?.filled)
    return { color: buy ? "#93c5fd" : "#EF4444", lineStyle: 2, title: `${data.direction} LIMIT` };
  return { color: "#93c5fd", lineStyle: 0, title: "Entry" };
}

export const TradeChartModal = ({ signal, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    if (!open || !signal) return;
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    setUpdates([]);
    api.get(`/signals/${signal.id}/updates`).then(({ data }) => { if (active) setUpdates(data || []); }).catch(() => {});
    api
      .get(`/signals/${signal.id}/chart`)
      .then(({ data }) => {
        if (!active) return;
        if (!data.candles || data.candles.length < 3) {
          setError("Storico non disponibile per questo trade (limite dati). Riprova tra poco.");
        } else {
          setData(data);
        }
      })
      .catch((e) => {
        logError("Trade chart error:", e);
        if (active) setError(formatError(e.response?.data?.detail) || "Grafico non disponibile. Riprova tra poco.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [open, signal]);

  const win = data?.status === "tp_hit";
  const isBe = data?.status === "be_hit";
  const isActive = data?.status === "active";
  const isBuy = data?.direction === "BUY";
  const isPendingLimit = data?.order_type === "LIMIT" && !data?.filled;
  const dirColor = isBuy ? "#10B981" : "#EF4444";
  // Breakeven active: either flagged by the backend field or shown via the advisory update.
  const beActive = isActive && (data?.breakeven || updates.some((u) => u.type === "partial_be_extend"));
  // Once at breakeven the effective TP is the extended one (+25%) and the effective SL is the entry.
  const effTp = (beActive || isBe) && data?.be_new_tp != null ? data.be_new_tp : data?.tp_price;
  const effSl = (beActive || isBe) ? data?.entry : data?.sl_price;
  const badge = beActive
    ? { cls: "bg-sky-400/20 text-sky-300", label: "Breakeven" }
    : orderBadge(data);
  const caption = chartCaption(data);

  const lastClose = data?.candles?.length ? data.candles[data.candles.length - 1].close : null;
  const currentPrice = data?.current_price ?? lastClose;
  const entry = entryLineStyle(data);
  const priceLines = data
    ? [
        { price: data.entry, color: entry.color, lineStyle: entry.lineStyle, title: entry.title },
        isActive && currentPrice != null && { price: currentPrice, color: "#F59E0B", lineStyle: 0, title: "Attuale" },
        effTp != null && { price: effTp, color: "#10B981", lineStyle: 2, title: beActive || isBe ? "TP+" : "TP" },
        // After breakeven the SL sits on the entry, so we skip a duplicate SL line there.
        !(beActive || isBe) && effSl != null && { price: effSl, color: "#EF4444", lineStyle: 2, title: "SL" },
      ].filter(Boolean)
    : [];

  // Closed trades: draw the entry→exit diagonal + exit marker. Breakeven closes AT the entry (0 P&L).
  const trades = data && !isActive
    ? [
        {
          startTime: data.entry_time,
          startPrice: data.entry,
          endTime: data.exit_time,
          endPrice: data.mt_close_price != null ? data.mt_close_price : (isBe ? data.entry : win ? effTp : data.sl_price),
          color: isBe ? "#38BDF8" : isBuy ? "#3B82F6" : "#FFFFFF",
          direction: data.direction,
          timeframe: data.timeframe,
          exitLabel: isBe ? "BE" : win ? "TP" : "SL",
        },
      ]
    : [];

  // Active & filled (or NOW): anchor the entry arrow to the exact entry price so it lines up with
  // the solid Entry line. Pending LIMIT orders show NO arrow until the price fills them.
  const markers = data && isActive && !isPendingLimit
    ? [
        { time: data.entry_time, price: data.entry, position: isBuy ? "belowBar" : "aboveBar", color: dirColor,
          shape: isBuy ? "arrowUp" : "arrowDown", text: `${data.direction} ${data.timeframe}` },
        { time: data.entry_time, price: data.entry, position: "inBar", color: dirColor, shape: "circle" },
      ]
    : [];

  const renderBody = () => {
    if (loading) {
      return (
        <div data-testid="trade-chart-loading" className="text-sm text-zinc-500">
          Caricamento grafico del trade...
        </div>
      );
    }
    if (error) {
      return (
        <div data-testid="trade-chart-error" className="text-sm text-zinc-500 text-center px-6">
          {error}
        </div>
      );
    }
    if (!data) return null;
    return (
      <div className="w-full">
        <CandlestickChart data={data.candles} priceLines={priceLines} markers={markers} trades={trades} />
        <p className="text-[11px] text-zinc-600 mt-3 font-mono">
          {caption}
        </p>
        {!isActive && (
          <p className="text-[11px] text-amber-500/60 mt-1">
            ⓘ Grafico sul timeframe del segnale (Capital.com). Mostrate 2 candele dopo il tocco del TP/SL.
          </p>
        )}
        {data?.warnings?.length > 0 && (
          <p data-testid="trade-chart-warning" className="text-[11px] text-red-400/80 mt-1">
            ⚠ Livelli TP/SL incoerenti con la scala del grafico Capital.com — alcuni riferimenti potrebbero non essere mostrati.
          </p>
        )}
        {updates.length > 0 && (
          <div className="mt-5" data-testid="trade-updates">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-400 mb-2">
              <Bell size={15} /> Aggiornamenti trade ({updates.length})
            </div>
            <div className="space-y-2">
              {updates.map((u) => (
                <div key={u.id} data-testid={`trade-update-${u.id}`} className="bg-amber-500/5 border border-amber-500/20 rounded-md p-3">
                  <div className="text-sm text-white">{u.message}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-mono">
                    <span className="text-zinc-400">SL → breakeven: <span className="text-blue-300">{fmtNum(u.new_sl)}</span></span>
                    <span className="text-zinc-400">Nuovo TP (+25%): <span className="text-emerald-300">{fmtNum(u.new_tp)}</span></span>
                    <span className="text-zinc-500">TP orig: {fmtNum(u.orig_tp)}</span>
                    {u.progress_pct != null && <span className="text-zinc-500">a {u.progress_pct}% del TP</span>}
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1.5">
                    {new Date(u.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="trade-chart-modal"
        onClick={(e) => e.stopPropagation()}
        className="max-w-3xl bg-[#0F0F0F] border-white/10 text-white"
      >
        <DialogHeader>
          <DialogDescription className="sr-only">
            Grafico del trade con entrata, livelli TP/SL e, per i trade conclusi, il punto di uscita.
          </DialogDescription>
          <DialogTitle className="flex items-center gap-3 text-base">
            <button
              data-testid="trade-chart-back-btn"
              onClick={() => onOpenChange(false)}
              className="flex items-center gap-1.5 text-xs font-mono text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={15} /> Indietro
            </button>
            <span className="font-mono text-white">{signal?.symbol}</span>
            {data?.seq != null && (
              <span data-testid="trade-chart-code" className="text-[10px] font-mono text-zinc-500 px-1.5 py-0.5 rounded-sm bg-white/5">
                ID {data.seq}
              </span>
            )}
            {data && (
              <>
                <span
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-mono ${
                    isBuy ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                  }`}
                >
                  {isBuy ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {data.direction}{data.order_type ? ` ${data.order_type === "LIMIT" && data.filled ? "FILLED" : data.order_type}` : ""} · {data.timeframe}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-sm text-xs font-mono uppercase tracking-wider ${badge?.cls || ""}`}
                >
                  {badge?.label}
                </span>
                {data.interval && (
                  <span className="text-[11px] font-mono text-zinc-500">
                    Grafico {INTERVAL_LABEL[data.interval] || data.interval}
                  </span>
                )}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 min-h-[420px] flex items-center justify-center">
          {renderBody()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
